import { useEffect, useState } from 'react';
import { CheckCircle2, TriangleAlert, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

export type ToastItem = {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  durationMs?: number;
};

export function showToast(toast: {
  type?: ToastType;
  title?: string;
  message: string;
  durationMs?: number;
}) {
  if (typeof window !== 'undefined') { 
    window.dispatchEvent(
      new CustomEvent('app:toast', {
        detail: {
          id: Math.random().toString(36).slice(2, 10),
          type: toast.type || 'success',
          title: toast.title,
          message: toast.message,
          durationMs: toast.durationMs ?? 4500,
        },
      })
    );
  }
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    function handleNewToast(e: Event) {
      const customEvent = e as CustomEvent<ToastItem>;
      if (!customEvent.detail) return;
      const newToast = customEvent.detail;

      setToasts((prev) => [...prev, newToast]);

      const duration = newToast.durationMs ?? 4500;
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== newToast.id));
      }, duration);
    }

    window.addEventListener('app:toast', handleNewToast);
    return () => window.removeEventListener('app:toast', handleNewToast);
  }, []);

  const dismissToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  if (toasts.length === 0) return null;

  return (
    <div className="toast-container" role="region" aria-label="Notifications" aria-live="polite">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`toast-card toast-card--${toast.type}`}
          role={toast.type === 'error' ? 'alert' : 'status'}
        >
          <div className="toast-icon-badge">
            {toast.type === 'success' && <CheckCircle2 size={18} />}
            {toast.type === 'error' && <TriangleAlert size={18} />}
            {toast.type === 'info' && <Info size={18} />}
          </div>

          <div className="toast-body">
            <div className="toast-title">
              {toast.title || (toast.type === 'success' ? 'Success' : toast.type === 'error' ? 'Error' : 'Notice')}
            </div>
            <div className="toast-message">{toast.message}</div>
          </div>

          <button
            type="button"
            className="toast-close-btn"
            onClick={() => dismissToast(toast.id)}
            aria-label="Dismiss notification"
          >
            <X size={15} />
          </button>

          <div className="toast-progress-track">
            <div
              className="toast-progress-fill"
              style={{ animationDuration: `${toast.durationMs ?? 4500}ms` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
